import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import {
  Ban,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  TriangleAlert,
  X,
} from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { llmApiKeysQueryOptions } from "../-secure-llm/-queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  allEntitiesQueryOptions,
  myPolicyAnalysisQueryOptions,
} from "@/lib/queries/entities";
import { api } from "@/lib/http";

export const Route = createFileRoute("/_app/gateway-keys/create")({
  component: CreateGatewayKeyPage,
});

type AdminPolicy = "Blocked" | "Redact" | "Allowed" | "Free";

type EntityConfig = {
  entityId: number;
  entityType: string;
  adminPolicy: AdminPolicy;
};

type CreateGatewayKeyPayload = {
  name: string;
  entities: Array<{
    entity_id: number;
    policy_mode: "Blocklist" | "Redactlist";
  }>;
  llm_api_key_ids: Array<string>;
};

type CreateGatewayKeyResponse = {
  id: string;
  name: string;
  description: string | null;
  status: string;
  revoked_at: string | null;
  last_used_at: string | null;
  created_at: string;
  updated_at: string | null;
  providers: Record<string, Array<string>>;
  entities: Array<{
    id: number;
    name: string;
    policy_mode: "Blocklist" | "Redactlist";
  }>;
  key: string;
};

const STEPS = [
  { number: 1, label: "Credential & Key" },
  { number: 2, label: "Entity Redaction" },
  { number: 3, label: "Review & Generate" },
] as const;

function Stepper({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex items-center">
      {STEPS.map((step, i) => {
        const isActive = currentStep === step.number;
        const isCompleted = currentStep > step.number;

        return (
          <React.Fragment key={step.number}>
            {isActive ? (
              <div className="flex shrink-0 items-center gap-2 rounded-full bg-[#0B1220] px-4 py-2 dark:bg-foreground">
                <span className="flex size-5 items-center justify-center rounded-full border border-white text-xs font-semibold text-white dark:text-background">
                  {step.number}
                </span>
                <span className="text-sm font-medium text-white dark:text-background">
                  {step.label}
                </span>
              </div>
            ) : (
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={cn(
                    "flex size-6 items-center justify-center rounded-full text-xs font-semibold",
                    isCompleted
                      ? "bg-emerald-500 text-white"
                      : "text-slate-400 dark:text-muted-foreground",
                  )}
                >
                  {isCompleted ? <Check className="size-3.5" /> : step.number}
                </span>
                <span className="text-sm text-muted-foreground">
                  {step.label}
                </span>
              </div>
            )}
            {i < STEPS.length - 1 && (
              <div
                className={cn(
                  "mx-3 h-px flex-1",
                  currentStep > step.number
                    ? "bg-emerald-500"
                    : "bg-slate-200 dark:bg-border",
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function CreateGatewayKeyPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = React.useState(1);
  const [keyName, setKeyName] = React.useState("");
  const [selectedCredentials, setSelectedCredentials] = React.useState<
    Set<string>
  >(new Set());

  const [entityOverrides, setEntityOverrides] = React.useState<
    Map<string, "Redact" | "Block">
  >(new Map());

  const [createdKey, setCreatedKey] =
    React.useState<CreateGatewayKeyResponse | null>(null);
  const [copied, setCopied] = React.useState(false);

  const models = useQuery({
    ...llmApiKeysQueryOptions({ page: 1, per_page: 100 }),
  });

  const policyAnalysisQuery = useQuery(myPolicyAnalysisQueryOptions());
  const allEntitiesQuery = useQuery(allEntitiesQueryOptions());

  const entityConfigs = React.useMemo<Array<EntityConfig>>(() => {
    const policyData = policyAnalysisQuery.data;
    const entities = allEntitiesQuery.data;
    if (!policyData || !entities) return [];

    const blockedSet = new Set(
      policyData.final_blocked_list.map((n) => n.toLowerCase()),
    );
    const redactedSet = new Set(
      policyData.final_redacted_list.map((n) => n.toLowerCase()),
    );
    const allowedSet = new Set(
      policyData.final_allowed_list.map((n) => n.toLowerCase()),
    );

    return entities.map((entity) => {
      const nameLower = entity.name.toLowerCase();
      let adminPolicy: AdminPolicy;
      if (blockedSet.has(nameLower)) {
        adminPolicy = "Blocked";
      } else if (redactedSet.has(nameLower)) {
        adminPolicy = "Redact";
      } else if (allowedSet.has(nameLower)) {
        adminPolicy = "Allowed";
      } else {
        adminPolicy = "Free";
      }
      return { entityId: entity.id, entityType: entity.name, adminPolicy };
    });
  }, [policyAnalysisQuery.data, allEntitiesQuery.data]);

  const createGatewayKeyMutation = useMutation({
    mutationFn: async (data: CreateGatewayKeyPayload) => {
      try {
        return api.raw
          .post("api/v1/my-gateway_keys/", { json: data })
          .json<CreateGatewayKeyResponse>();
      } catch (err) {
        let message =
          (err as Error).message ||
          "Failed to create gateway key. Please try again.";
        if (err instanceof HTTPError) {
          const body = await err.response.json().catch(() => null);
          if (body?.detail && typeof body.detail === "string")
            message = body.detail;
        }
        throw new Error(message);
      }
    },
    onSuccess: (data) => {
      setCreatedKey(data);
    },
  });

  const toggleCredential = (id: string) => {
    setSelectedCredentials((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleOverride = (entityType: string, action: "Redact" | "Block") => {
    setEntityOverrides((prev) => {
      const next = new Map(prev);
      if (next.get(entityType) === action) {
        next.delete(entityType);
      } else {
        next.set(entityType, action);
      }
      return next;
    });
  };

  const removeOverride = (entityType: string) => {
    setEntityOverrides((prev) => {
      const next = new Map(prev);
      next.delete(entityType);
      return next;
    });
  };

  const goBack = () => {
    if (currentStep === 1) {
      navigate({ to: "/gateway-keys" });
    } else {
      setCurrentStep((s) => s - 1);
    }
  };

  const canProceed =
    currentStep === 1
      ? keyName.trim().length > 0 && selectedCredentials.size > 0
      : true;

  const goNext = () => {
    if (currentStep < STEPS.length && canProceed) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleGenerate = () => {
    type EntityEntry = {
      entity_id: number;
      policy_mode: "Blocklist" | "Redactlist";
    };
    const entities: Array<EntityEntry> = [];

    for (const config of entityConfigs) {
      if (config.adminPolicy === "Blocked") {
        entities.push({ entity_id: config.entityId, policy_mode: "Blocklist" });
        continue;
      }
      const override = entityOverrides.get(config.entityType);
      if (override === "Block") {
        entities.push({ entity_id: config.entityId, policy_mode: "Blocklist" });
      } else if (override === "Redact" || config.adminPolicy === "Redact") {
        entities.push({
          entity_id: config.entityId,
          policy_mode: "Redactlist",
        });
      }
    }

    createGatewayKeyMutation.mutate({
      name: keyName,
      entities,
      llm_api_key_ids: Array.from(selectedCredentials),
    });
  };

  const resolvedCredentialNames = React.useMemo(() => {
    const items = models.data?.items ?? [];
    return Array.from(selectedCredentials)
      .map((id) => items.find((m) => m.id === id)?.name ?? id)
      .join(", ");
  }, [selectedCredentials, models.data]);

  const handleCopyKey = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-white shadow-sm dark:bg-card">
        <div className="flex items-center justify-between border-b px-6 py-5">
          <div>
            <h2 className="text-lg font-semibold">Create Gateway Key</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Select a provider, configure models and entity redaction policy
            </p>
          </div>
        </div>

        <div className="px-6 py-5">
          <Stepper currentStep={currentStep} />
        </div>

        <div className="border-t px-6 py-6">
          {currentStep === 1 && (
            <StepCredentialAndKey
              keyName={keyName}
              onKeyNameChange={setKeyName}
              selectedCredentials={selectedCredentials}
              onToggleCredential={toggleCredential}
              models={models.data?.items ?? []}
            />
          )}
          {currentStep === 2 && (
            <StepEntityRedaction
              entityConfigs={entityConfigs}
              isLoading={
                policyAnalysisQuery.isLoading || allEntitiesQuery.isLoading
              }
              overrides={entityOverrides}
              onToggleOverride={toggleOverride}
              onRemoveOverride={removeOverride}
            />
          )}
          {currentStep === 3 && (
            <StepReviewGenerate
              keyName={keyName}
              selectedCredentials={selectedCredentials}
              models={models.data?.items ?? []}
              entityOverrides={entityOverrides}
              entityConfigs={entityConfigs}
            />
          )}
        </div>

        <div className="border-t px-6 py-4">
          {createGatewayKeyMutation.isError && (
            <p className="mb-3 text-sm text-red-600 dark:text-red-400">
              {createGatewayKeyMutation.error.message}
            </p>
          )}
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={goBack}>
              {currentStep > 1 && <ChevronLeft className="size-4" />}
              {currentStep > 1 ? "Back" : "Cancel"}
            </Button>
            {currentStep === STEPS.length ? (
              <Button
                onClick={handleGenerate}
                disabled={createGatewayKeyMutation.isPending}
              >
                {createGatewayKeyMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <KeyRound className="size-4" />
                )}
                Generate Key
              </Button>
            ) : (
              <Button onClick={goNext} disabled={!canProceed}>
                Next
                <ChevronRight className="size-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={createdKey !== null}
        onOpenChange={(open) => {
          if (!open) navigate({ to: "/gateway-keys" });
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/40">
                <CheckCircle2 className="size-7 text-emerald-500" />
              </div>
              <div>
                <DialogTitle className="text-xl">
                  Gateway Key Generated
                </DialogTitle>
                <DialogDescription className="mt-1">
                  Your new key is now active and ready to use.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Your Gateway Key
            </p>

            <div className="flex items-center gap-2 rounded-xl bg-[#0B1220] px-4 py-3 dark:bg-foreground">
              <code className="min-w-0 flex-1 font-mono text-sm break-all text-white dark:text-background">
                {createdKey?.key}
              </code>
              <button
                type="button"
                onClick={handleCopyKey}
                className={cn(
                  "flex size-8 shrink-0 items-center justify-center rounded-md transition-colors",
                  copied
                    ? "text-emerald-400"
                    : "text-slate-400 hover:bg-white/10 hover:text-white dark:text-muted-foreground dark:hover:bg-black/10 dark:hover:text-background",
                )}
              >
                {copied ? (
                  <Check className="size-4" />
                ) : (
                  <Copy className="size-4" />
                )}
              </button>
            </div>

            <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
              <TriangleAlert className="size-3.5 shrink-0" />
              Copy this key now — it won't be shown again.
            </p>

            <div className="divide-y divide-border overflow-hidden rounded-lg border bg-slate-50/80 dark:bg-muted/40">
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-sm text-muted-foreground">Name</span>
                <span className="text-sm font-medium">{createdKey?.name}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-sm text-muted-foreground">
                  Credential
                </span>
                <span className="max-w-[60%] truncate text-right text-sm font-medium">
                  {resolvedCredentialNames}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-sm text-muted-foreground">
                  Entities Controlled
                </span>
                <span className="text-sm font-medium">
                  {createdKey?.entities.length ?? 0} types
                </span>
              </div>
            </div>

            <Button
              className="w-full"
              onClick={() => navigate({ to: "/gateway-keys" })}
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StepCredentialAndKey({
  keyName,
  onKeyNameChange,
  selectedCredentials,
  onToggleCredential,
  models,
}: {
  keyName: string;
  onKeyNameChange: (v: string) => void;
  selectedCredentials: Set<string>;
  onToggleCredential: (id: string) => void;
  models: Array<{
    id: string;
    name: string;
    description: string;
    api_key: string;
    llm_models: Array<{
      id: string;
      name: string;
      description: string;
      provider: string;
    }>;
  }>;
}) {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
          Key Name
        </label>
        <Input
          value={keyName}
          onChange={(e) => onKeyNameChange(e.target.value)}
          placeholder="e.g. Production OpenAI Key"
        />
      </div>

      <div className="space-y-3">
        <div>
          <h3 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
            Select Credentials
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Choose one or more credentials your admin has configured for your
            organization. All their models will be available through this key.
          </p>
        </div>

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80 text-[11px] tracking-wider text-muted-foreground uppercase hover:bg-slate-50/80 dark:bg-muted dark:hover:bg-muted">
                <TableHead className="w-10 px-4" />
                <TableHead className="px-4 text-muted-foreground">
                  Credential
                </TableHead>
                <TableHead className="px-4 text-muted-foreground">
                  Name
                </TableHead>
                <TableHead className="hidden px-4 text-muted-foreground md:table-cell">
                  Description
                </TableHead>
                <TableHead className="hidden px-4 text-muted-foreground lg:table-cell">
                  Models
                </TableHead>
                <TableHead className="px-4 text-right text-muted-foreground">
                  API Key
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {models.map((model, i) => {
                const isSelected = selectedCredentials.has(model.id);
                const firstModel = model.llm_models[0];
                return (
                  <TableRow
                    key={model.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onToggleCredential(model.id)}
                    className={cn(
                      "cursor-pointer",
                      isSelected ? "bg-slate-50 dark:bg-muted/50" : "",
                      i % 2 === 1 && !isSelected ? "bg-white dark:bg-card" : "",
                    )}
                  >
                    <TableCell className="px-4 py-3.5">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleCredential(model.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell className="px-4 py-3.5 font-medium">
                      {firstModel.provider}
                    </TableCell>
                    <TableCell className="px-4 py-3.5 font-medium">
                      {model.name}
                    </TableCell>
                    <TableCell className="px-4 py-3.5">
                      {model.description}
                    </TableCell>
                    <TableCell className="hidden px-4 py-3.5 text-muted-foreground md:table-cell">
                      {model.llm_models.map((m) => m.name).join(", ")}
                    </TableCell>
                    <TableCell className="hidden px-4 py-3.5 text-muted-foreground lg:table-cell">
                      {model.api_key.slice(0, 4) +
                        "****" +
                        model.api_key.slice(-4)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

const adminPolicyColor: Record<AdminPolicy, { dot: string; text: string }> = {
  Blocked: {
    dot: "bg-red-500",
    text: "text-red-600 dark:text-red-400",
  },
  Redact: {
    dot: "bg-amber-500",
    text: "text-amber-600 dark:text-amber-400",
  },
  Allowed: {
    dot: "bg-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
  },
  Free: {
    dot: "bg-slate-400",
    text: "text-slate-500 dark:text-slate-400",
  },
};

const POLICY_LEGEND: Array<{ key: AdminPolicy; label: string }> = [
  { key: "Blocked", label: "Blocked — fixed, cannot change" },
  { key: "Redact", label: "Redact — can downgrade to Block" },
  { key: "Allowed", label: "Allowed — can set to Redact or Block" },
  { key: "Free", label: "Free — full control" },
];

function StepEntityRedaction({
  entityConfigs,
  isLoading,
  overrides,
  onToggleOverride,
  onRemoveOverride,
}: {
  entityConfigs: Array<EntityConfig>;
  isLoading: boolean;
  overrides: Map<string, "Redact" | "Block">;
  onToggleOverride: (entityType: string, action: "Redact" | "Block") => void;
  onRemoveOverride: (entityType: string) => void;
}) {
  const overrideEntries = entityConfigs.filter((e) =>
    overrides.has(e.entityType),
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 size-5 animate-spin" />
        Loading entity policies…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-teal-200 bg-teal-50/60 px-4 py-3 text-sm dark:border-teal-900 dark:bg-teal-950/30">
        <span className="font-medium text-foreground">Admin policies:</span>
        {POLICY_LEGEND.map((p) => (
          <span key={p.key} className="flex items-center gap-1.5">
            <span
              className={cn("size-2 rounded-full", adminPolicyColor[p.key].dot)}
            />
            <span className="text-muted-foreground">{p.label}</span>
          </span>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border">
        <div className="max-h-[420px] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10">
              <TableRow className="bg-slate-50 text-[11px] tracking-wider text-muted-foreground uppercase hover:bg-slate-50 dark:bg-muted dark:hover:bg-muted">
                <TableHead className="px-4 text-muted-foreground">
                  Entity Type
                </TableHead>
                <TableHead className="px-4 text-muted-foreground">
                  Admin Policy
                </TableHead>
                <TableHead className="w-20 px-4 text-center text-muted-foreground">
                  Redact
                </TableHead>
                <TableHead className="w-20 px-4 text-center text-muted-foreground">
                  Block
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...entityConfigs]
                .sort((a, b) => {
                  const order = { Allowed: 0, Free: 0, Blocked: 1, Redact: 2 };
                  return (
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    (order[a.adminPolicy] ?? 3) - (order[b.adminPolicy] ?? 3)
                  );
                })
                .map((entity, i) => {
                  const isBlocked = entity.adminPolicy === "Blocked";
                  const isAdminRedact = entity.adminPolicy === "Redact";
                  const override = overrides.get(entity.entityType);

                  const canRedact =
                    entity.adminPolicy === "Allowed" ||
                    entity.adminPolicy === "Free";
                  const canBlock = entity.adminPolicy !== "Blocked";

                  return (
                    <TableRow
                      key={entity.entityType}
                      className={cn(
                        override
                          ? "bg-slate-100 hover:bg-slate-100 dark:bg-muted/60 dark:hover:bg-muted/60"
                          : isBlocked
                            ? "bg-red-50/60 hover:bg-red-50/60 dark:bg-red-950/20 dark:hover:bg-red-950/20"
                            : isAdminRedact
                              ? "bg-amber-50/60 hover:bg-amber-50/60 dark:bg-amber-950/20 dark:hover:bg-amber-950/20"
                              : i % 2 === 1
                                ? "bg-white dark:bg-card"
                                : "",
                      )}
                    >
                      <TableCell
                        className={cn(
                          "px-4 py-3",
                          isBlocked && "text-muted-foreground/50",
                        )}
                      >
                        <span className="flex items-center gap-2">
                          {isBlocked && (
                            <Lock className="size-3.5 shrink-0 text-red-400 dark:text-red-500/70" />
                          )}
                          {entity.entityType}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <span
                          className={cn(
                            "flex items-center gap-1.5 font-medium",
                            adminPolicyColor[entity.adminPolicy].text,
                          )}
                        >
                          <span
                            className={cn(
                              "size-2 rounded-full",
                              adminPolicyColor[entity.adminPolicy].dot,
                            )}
                          />
                          {entity.adminPolicy}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-center">
                        {isAdminRedact ? (
                          // Admin has already applied Redact — show as pre-selected (non-interactive)
                          <span className="inline-flex size-8 items-center justify-center rounded-full bg-amber-500 text-white shadow-sm">
                            <EyeOff className="size-4" />
                          </span>
                        ) : canRedact ? (
                          <button
                            type="button"
                            onClick={() =>
                              onToggleOverride(entity.entityType, "Redact")
                            }
                            className={cn(
                              "inline-flex size-8 items-center justify-center rounded-full transition-colors",
                              override === "Redact"
                                ? "bg-amber-500 text-white shadow-sm"
                                : "border border-slate-200 text-muted-foreground/40 hover:border-amber-300 hover:text-amber-500 dark:border-border dark:hover:border-amber-700 dark:hover:text-amber-400",
                            )}
                          >
                            <EyeOff className="size-4" />
                          </button>
                        ) : (
                          <span className="text-muted-foreground/30">—</span>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-center">
                        {isBlocked ? (
                          // Admin has already applied Block — show as pre-selected (non-interactive)
                          <span className="inline-flex size-8 items-center justify-center rounded-full bg-red-500 text-white shadow-sm">
                            <Ban className="size-4" />
                          </span>
                        ) : canBlock ? (
                          <button
                            type="button"
                            onClick={() =>
                              onToggleOverride(entity.entityType, "Block")
                            }
                            className={cn(
                              "inline-flex size-8 items-center justify-center rounded-full transition-colors",
                              override === "Block"
                                ? "bg-red-500 text-white shadow-sm"
                                : "border border-slate-200 text-muted-foreground/40 hover:border-red-300 hover:text-red-500 dark:border-border dark:hover:border-red-700 dark:hover:text-red-400",
                            )}
                          >
                            <Ban className="size-4" />
                          </button>
                        ) : (
                          <span className="text-muted-foreground/30">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      </div>

      {overrideEntries.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/20">
          <div className="mb-2.5 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            Your Overrides ({overrideEntries.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {overrideEntries.map((entity) => {
              const action = overrides.get(entity.entityType)!;
              const isBlock = action === "Block";
              return (
                <span
                  key={entity.entityType}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full py-1 pr-1.5 pl-2.5 text-sm font-medium",
                    isBlock
                      ? "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300"
                      : "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
                  )}
                >
                  {isBlock ? (
                    <Ban className="size-3" />
                  ) : (
                    <EyeOff className="size-3" />
                  )}
                  {entity.entityType}
                  <button
                    type="button"
                    onClick={() => onRemoveOverride(entity.entityType)}
                    className="ml-0.5 inline-flex size-5 items-center justify-center rounded-full transition-colors hover:bg-black/10 dark:hover:bg-white/10"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StepReviewGenerate({
  keyName,
  selectedCredentials,
  models,
  entityOverrides,
  entityConfigs,
}: {
  keyName: string;
  selectedCredentials: Set<string>;
  models: Array<{ id: string; name: string }>;
  entityOverrides: Map<string, "Redact" | "Block">;
  entityConfigs: Array<EntityConfig>;
}) {
  const credentialNames = Array.from(selectedCredentials)
    .map((id) => models.find((m) => m.id === id)?.name ?? id)
    .join(", ");

  const allControlledEntries = entityConfigs.filter(
    (e) =>
      e.adminPolicy === "Blocked" ||
      e.adminPolicy === "Redact" ||
      entityOverrides.has(e.entityType),
  );

  return (
    <div className="space-y-5">
      <h3 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
        Review Configuration
      </h3>

      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableBody>
            <TableRow className="hover:bg-transparent">
              <TableCell className="px-4 py-3 text-muted-foreground">
                Key Name
              </TableCell>
              <TableCell className="px-4 py-3 text-right font-medium">
                {keyName}
              </TableCell>
            </TableRow>
            <TableRow className="hover:bg-transparent">
              <TableCell className="px-4 py-3 text-muted-foreground">
                Credentials
              </TableCell>
              <TableCell className="px-4 py-3 text-right font-medium">
                {credentialNames}
              </TableCell>
            </TableRow>
            <TableRow className="hover:bg-transparent">
              <TableCell className="px-4 py-3 text-muted-foreground">
                Entities Controlled
              </TableCell>
              <TableCell className="px-4 py-3 text-right font-medium">
                {allControlledEntries.length} types
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {allControlledEntries.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {allControlledEntries.map((entity) => {
            const override = entityOverrides.get(entity.entityType);
            const effectiveAction =
              override ??
              (entity.adminPolicy === "Blocked" ? "Block" : "Redact");
            const isBlock = effectiveAction === "Block";
            return (
              <span
                key={entity.entityType}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full py-1 pr-3 pl-2.5 text-sm font-medium",
                  isBlock
                    ? "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
                )}
              >
                {isBlock ? (
                  <Ban className="size-3" />
                ) : (
                  <EyeOff className="size-3" />
                )}
                {entity.entityType}
              </span>
            );
          })}
        </div>
      )}

      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/20">
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-500" />
        <p className="text-sm text-amber-700 dark:text-amber-300">
          This gateway key inherits your current entity access permissions. If
          an admin later changes your privileges, they can propagate the change
          selectively to your existing keys.
        </p>
      </div>
    </div>
  );
}
